/**
 * Group Applications — a Motzu admin's queue for buying-group and
 * head-office applications (e.g. Fotosource Canada), plus every group Motzu
 * already runs by hand.
 *
 * A self-serve signup (`onboardingService.completeGroupOnboarding`) creates
 * its parent organization and lands here `pending`: the group grants its
 * managers nothing on any member store (`listGroupsManagedBy` only returns
 * `active` groups) until approved here. Rejecting leaves an auditable
 * `rejected` record rather than deleting anything.
 */

import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Stack, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useToast } from '../../contexts/ToastContext';
import { InlineMessage, StatusChip, TableSkeleton, statusTone } from '../../components/ui';
import { bvColor, bvContentSx, bvPageSx, bvRadius, bvTransition, bvType, tabular } from './brandVendorsTokens';
import { BvButton, BvCard, BvEmptyState, BvPageHeader, DefinitionRows } from './components/BrandVendorsPrimitives';

interface AdminGroupRow {
  id: string;
  name: string;
  kind: 'buying_group' | 'multi_store';
  parentOrganizationId: string;
  parentOrganizationName: string | null;
  parentOrganizationType: string | null;
  status: 'pending' | 'active' | 'rejected' | 'archived';
  createdAt: string | null;
  memberCount: number;
  rejectionReason?: string | null;
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

const KIND_LABEL: Record<AdminGroupRow['kind'], string> = {
  buying_group: 'Buying group',
  multi_store: 'Multi-store head office',
};

const STATUS_TABS: Array<{ key: 'pending' | 'active' | 'rejected' | 'all'; label: string }> = [
  { key: 'pending', label: 'Pending review' },
  { key: 'active', label: 'Active' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

export const BrandVendorsGroupApplicationsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'pending' | 'active' | 'rejected' | 'all'>('pending');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // One fetch of every group; the tab filters and counts are derived from it so
  // each tab can show how many it holds without a request per tab.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'organization-groups'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/admin/groups', { headers });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to load organization groups'));
      }
      return response.json();
    },
  });

  const allRows: AdminGroupRow[] = useMemo(() => data?.data || [], [data]);
  const tabCounts = useMemo(
    () => ({
      pending: allRows.filter((row) => row.status === 'pending').length,
      active: allRows.filter((row) => row.status === 'active').length,
      rejected: allRows.filter((row) => row.status === 'rejected').length,
      all: allRows.length,
    }),
    [allRows]
  );
  const rows = useMemo(
    () => (tab === 'all' ? allRows : allRows.filter((row) => row.status === tab)),
    [allRows, tab]
  );

  const approveMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/admin/groups/${groupId}/approve`, { method: 'POST', headers });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to approve this group'));
      }
      return response.json();
    },
    onSuccess: () => {
      showToast('Group approved', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'organization-groups'] });
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Failed to approve this group', 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ groupId, reason }: { groupId: string; reason: string }) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/admin/groups/${groupId}/reject`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: reason || undefined }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to reject this group'));
      }
      return response.json();
    },
    onSuccess: () => {
      showToast('Group rejected', 'success');
      setRejectingId(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'organization-groups'] });
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Failed to reject this group', 'error');
    },
  });

  return (
    <Box sx={embedded ? undefined : bvPageSx}>
      <Box sx={embedded ? undefined : bvContentSx}>
        {!embedded && (
          <BvPageHeader
            title="Group Applications"
            description="Buying groups and head offices (e.g. Fotosource Canada) that manage member stores. A group holds no catalog of its own and grants its managers nothing on any member store until approved here."
          />
        )}

        <Stack direction="row" spacing={1} role="group" aria-label="Filter applications by status" sx={{ mt: embedded ? 0 : '18px', mb: '14px', flexWrap: 'wrap', gap: '8px' }}>
          {STATUS_TABS.map((t) => {
            const on = tab === t.key;
            return (
              <ButtonBase
                key={t.key}
                aria-pressed={on}
                onClick={() => setTab(t.key)}
                sx={{
                  height: 28,
                  px: '12px',
                  gap: '6px',
                  borderRadius: `${bvRadius.xsmall}px`,
                  ...bvType.buttonSmall,
                  // Selected = solid accent; hover only tints the border, so a hovered tab never reads as selected.
                  color: on ? bvColor.surface : bvColor.ink3,
                  bgcolor: on ? bvColor.accent : bvColor.surface,
                  border: `1px solid ${on ? bvColor.accent : bvColor.border}`,
                  transition: bvTransition,
                  '&:hover': on
                    ? { bgcolor: bvColor.accentHover, borderColor: bvColor.accentHover }
                    : { bgcolor: bvColor.page, borderColor: bvColor.accentTint2 },
                  '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                }}
              >
                {t.label}
                <Box component="span" sx={{ opacity: on ? 0.85 : 1, color: on ? 'inherit' : bvColor.muted, ...tabular }}>
                  {isLoading ? '–' : tabCounts[t.key]}
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>

        <BvCard>
          {isLoading ? (
            <TableSkeleton rows={4} columns={5} />
          ) : isError ? (
            <InlineMessage severity="error">
              {error instanceof Error ? error.message : 'Failed to load organization groups'}
            </InlineMessage>
          ) : rows.length === 0 ? (
            <BvEmptyState
              headline={tab === 'pending' ? 'No pending applications' : 'No groups here'}
              note={
                tab === 'pending'
                  ? 'Buying-group and head-office signups land here for review before they can manage any stores.'
                  : undefined
              }
            />
          ) : (
            <Stack divider={<Box sx={{ borderTop: `1px solid ${bvColor.borderSubtle}` }} />}>
              {rows.map((group) => (
                <Box key={group.id} sx={{ p: '16px' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Box>
                      <Box sx={{ ...bvType.body, fontWeight: 600, color: bvColor.ink }}>{group.name}</Box>
                      <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '2px' }}>
                        {KIND_LABEL[group.kind]} · run by {group.parentOrganizationName || group.parentOrganizationId}
                        {group.parentOrganizationType === 'group' ? ' (no catalog of its own)' : ''}
                      </Box>
                    </Box>
                    <StatusChip label={group.status} tone={statusTone(group.status)} />
                  </Stack>

                  <DefinitionRows
                    sx={{ mt: '10px' }}
                    rows={[
                      { label: 'Members', value: `${group.memberCount} active` },
                      ...(group.status === 'rejected' && group.rejectionReason
                        ? [{ label: 'Reason', value: group.rejectionReason }]
                        : []),
                    ]}
                  />

                  {group.status === 'pending' && (
                    <Stack direction="row" spacing={1} sx={{ mt: '12px' }} alignItems="center">
                      <BvButton
                        tone="primary"
                        size="sm"
                        onClick={() => approveMutation.mutate(group.id)}
                        disabled={approveMutation.isPending}
                      >
                        Approve
                      </BvButton>
                      {rejectingId === group.id ? (
                        <>
                          <TextField
                            size="small"
                            placeholder="Reason (optional)"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            sx={{ minWidth: 220 }}
                          />
                          <BvButton
                            tone="danger"
                            size="sm"
                            onClick={() => rejectMutation.mutate({ groupId: group.id, reason: rejectReason })}
                            disabled={rejectMutation.isPending}
                          >
                            Confirm reject
                          </BvButton>
                          <BvButton
                            size="sm"
                            onClick={() => {
                              setRejectingId(null);
                              setRejectReason('');
                            }}
                          >
                            Cancel
                          </BvButton>
                        </>
                      ) : (
                        <BvButton size="sm" onClick={() => setRejectingId(group.id)}>
                          Reject
                        </BvButton>
                      )}
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </BvCard>
      </Box>
    </Box>
  );
};

export default BrandVendorsGroupApplicationsPage;
